import React, { useCallback } from 'react';
import { Button, ButtonFill, ComponentColor, DialogContent, DialogFooter } from '@tcc/fds';
import { getMessage } from '@tcc/i18n';
import { Nullable } from '@tcc/common';
import { RegionReportList } from '../../RegionReportList/RegionReportList';
import type { BaseFieldRegionReportModalParams, RootLevelDetails} from '../types/modals';
import { RegionReportModal } from '../RegionReportModal';
import { ArdFieldRegionReport } from '../../../../../types/FieldRegionReport';
import { SetItemCallback } from '../useRegionReportController';
import { YieldRegionReport } from '../../../../../types/Reports';

type RegionReportListProps = React.ComponentProps<typeof RegionReportList>;

export interface ReportContentViewProps extends BaseFieldRegionReportModalParams {
  activity: RegionReportListProps['activity'] | null | undefined;
  cropCode: RegionReportListProps['cropCode'];
  fieldBoundary: RegionReportListProps['fieldBoundary'];
  fieldName: RegionReportListProps['fieldName'];
  index: number;
  isAirSeeder: boolean;
  isDetailView: boolean;
  isListView: boolean;
  itemData: Nullable<RootLevelDetails>;
  locale: RegionReportListProps['locale'];
  onCancelClick: () => void;
  onClickItem: SetItemCallback;
  onNotePadClick: RegionReportListProps['onRegionButtonClick'];
  onStep2Click: () => void;
  reportData: YieldRegionReport | ArdFieldRegionReport;
  onDeleteConfirm: () => void;
  onRegionButtonClick: () => void;
  saveClick: () => void;
  showSaveButton: boolean;
}

export const ReportContentView = ({
  activity,
  cropCode,
  fieldBoundary,
  fieldName,
  index,
  isAirSeeder,
  isDetailView,
  isListView,
  itemData,
  locale,
  onCancelClick,
  onDeleteConfirm,
  onClickItem,
  onNotePadClick,
  onStep2Click,
  reportData,
  saveClick,
  showSaveButton,
  dialogTestId,
  onOpenChange,
}: ReportContentViewProps) => {
  const stepBackMethod = useCallback(() => {
    if (isDetailView) {
      onStep2Click();
    }
  }, [isDetailView, onStep2Click]);
  const hasStepBackMethod = !!isDetailView;

  return (
    <RegionReportModal
      dialogTestId={dialogTestId}
      modalViewKey='dmb-region-report-content'
      onOpenChange={onOpenChange}
      onStepBack={hasStepBackMethod ? stepBackMethod : undefined}
      >
      <DialogContent key={`${index}-report-content`} testId='region-report-form'>
        {activity && reportData && isListView && (
          <RegionReportList
            activity={activity}
            cropCode={cropCode}
            data={reportData}
            fieldBoundary={fieldBoundary}
            fieldName={fieldName}
            index={index}
            isAirSeeder={isAirSeeder}
            locale={locale}
            onClickItem={onClickItem}
            onRegionButtonClick={onNotePadClick}
          />
        )}

        {activity && itemData && isDetailView && (
          <RegionReportList
            activity={activity}
            cropCode={cropCode}
            data={itemData}
            fieldBoundary={fieldBoundary}
            fieldName={fieldName}
            index={index}
            isAirSeeder={isAirSeeder}
            locale={locale}
            onRegionButtonClick={onNotePadClick}
            detailView
          />
        )}
      </DialogContent>
      <DialogFooter>
        <Button
          testId='frr-footer-cancel-button'
          onClick={onCancelClick}
          fill={ButtonFill.Outline}
          color={ComponentColor.Primary}>
            {getMessage('cancel')}
        </Button>

        {showSaveButton && (
          <Button
            testId='frr-footer-save-button'
            onClick={saveClick}
            fill={ButtonFill.Solid}
            color={ComponentColor.Primary}>
              {getMessage('regions.report.modal.saveReport')}
          </Button>
        )}

      {!showSaveButton && (
          <Button
            testId='frr-footer-delete-button'
            onClick={onDeleteConfirm}
            fill={ButtonFill.Solid}
            color={ComponentColor.Error}>
              {getMessage('regions.report.modal.delete.report')}
          </Button>
      )}
      </DialogFooter>
    </RegionReportModal>
  );
};
